import React, { useState, useEffect } from 'react';
import { Progress } from 'antd';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconUpload, IconDelete } from '@nxsys/components/AppIconSvg';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { getFileNameFromUrl, getFileTypeFromUrl } from '@nxsys/utils';
import { StyledUploadFile, StyledFilePreviewContainer } from './index.styled';
import { formatFileTypeToIcon } from '@nxsys/helpers/FileHelper';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showToast } from '@nxsys/utils/toast';

const IMAGE_DIMENSION_DIFF = 20;

/**
 * @param maxFileSize - in MB default 50MB
 */

type AppUploadFileProps = {
  urlFile?: string;
  multiple?: boolean;
  onChange: (file: File) => void;
  maxFileSize?: number;
  onFileSizeError?: (file: File, maxSize: number) => void;
  hideDelete?: boolean;
  onClickFile?: () => void;
  imageDimensions?: { width: number; height: number };
};

const AppUploadFile: React.FC<AppUploadFileProps> = ({
  urlFile,
  multiple = false,
  onChange,
  maxFileSize = 50, // 50MB default
  onFileSizeError,
  hideDelete = false,
  onClickFile,
  imageDimensions,
  ...rest
}: AppUploadFileProps) => {
  const { formatMessage } = useTypedIntl();
  const [fileInfo, setFileInfo] = useState<{
    url: string;
    name: string;
    type: string;
    size: number;
  } | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [isShowProgress, setIsShowProgress] = useState<boolean>(true);

  useEffect(() => {
    if (urlFile) {
      const name = getFileNameFromUrl(urlFile);
      const type = getFileTypeFromUrl(urlFile);
      setFileInfo({ url: urlFile, name, type, size: 0 });
      setIsShowProgress(false);
    }
  }, [urlFile]);

  const props = {
    name: 'file',
    multiple,
    beforeUpload: async (file: File) => {
      // Validate file size first
      if (!validateFileSize(file)) {
        return false;
      }

      if (!(await validateImageDimensions(file))) {
        return false;
      }

      const url = URL.createObjectURL(file);
      setFileInfo({ url, name: file.name, type: file.type, size: file.size });
      setProgress(0);

      let percent = 0;
      const interval = setInterval(() => {
        percent += 10;
        setProgress(percent);
        if (percent >= 100) clearInterval(interval);
      }, 100);
      onChange(file);
      setIsShowProgress(true);
      return false;
    },
    // onDrop(e: any) {
    //   console.log("Dropped files", e.dataTransfer.files);
    // },
  };
  const formatSize = (size: number) => {
    if (size >= 1024 * 1024) return (size / (1024 * 1024)).toFixed(1) + ' MB';
    if (size >= 1024) return (size / 1024).toFixed(1) + ' KB';
    return size + ' B';
  };

  const validateFileSize = (file: File): boolean => {
    if (file.size > maxFileSize * 1024 * 1024) {
      const maxSizeFormatted = formatSize(maxFileSize * 1024 * 1024);

      showToast({
        type: 'error',
        message: formatMessage(
          { id: 'upload.fileSizeExceeded' },
          { maxSize: maxSizeFormatted },
        ),
      });

      if (onFileSizeError) {
        onFileSizeError(file, maxFileSize);
      }

      return false;
    }
    return true;
  };

  const validateImageDimensions = (file: File) => {
    return new Promise((resolve, reject) => {
      if (!imageDimensions) {
        resolve(true);
        return;
      }

      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        resolve(true); // Not an image, skip validation
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img: any = new Image();
        img.onload = () => {
          if (
            Math.abs(img.width - imageDimensions.width) <=
              IMAGE_DIMENSION_DIFF &&
            Math.abs(img.height - imageDimensions.height) <=
              IMAGE_DIMENSION_DIFF
          ) {
            resolve(true);
          } else {
            showToast({
              type: 'error',
              message: `Image size must be ${imageDimensions.width}x${imageDimensions.height} pixels. Current size: ${img.width}x${img.height} pixels`,
            });
            reject(false);
          }
        };
        img.onerror = () => {
          showToast({
            type: 'error',
            message: 'Unable to read image',
          });
          reject(false);
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        showToast({
          type: 'error',
          message: 'Unable to read file',
        });
        reject(false);
      };
      reader.readAsDataURL(file);
    });
  };

  const renderPreview = () => {
    if (!fileInfo) return null;
    if (fileInfo.type.startsWith('image/')) {
      return (
        <div className="file-preview">
          <img src={fileInfo.url} alt={fileInfo.name} />
          <div className="file-preview-meta">
            <span className="file-preview-name">{fileInfo.name}</span>
            <span className="file-preview-size">
              {formatSize(fileInfo.size)}
            </span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="file-preview">
          {formatFileTypeToIcon(fileInfo.type)}
          <div className="file-preview-meta">
            <span className="file-preview-name">{fileInfo.name}</span>
            {fileInfo.size > 0 ? (
              <span className="file-preview-size">
                {formatSize(fileInfo.size)}
              </span>
            ) : null}
          </div>
        </div>
      );
    }
  };
  return (
    <div className="max-w-full cursor-pointer" onClick={() => onClickFile?.()}>
      {!fileInfo ? (
        <StyledUploadFile {...props} {...rest} showUploadList={false}>
          <p className="ant-upload-drag-icon">
            <IconUpload />
          </p>
          <p className="ant-upload-text">
            <span>Click to upload</span> or drag and drop
          </p>
          <p className="ant-upload-hint">
            (
            {formatMessage(
              { id: 'upload.fileSizeLimit' },
              { maxSize: maxFileSize },
            )}
            MB )
          </p>
        </StyledUploadFile>
      ) : (
        <StyledFilePreviewContainer>
          {renderPreview()}
          {!hideDelete && (
            <AppIconButton
              icon={<IconDelete width={20} height={20} />}
              onClick={() => {
                setFileInfo(null);
                onChange(null as any);
              }}
              className="translate-x-[2px]"
            />
          )}
          {isShowProgress && (
            <Progress
              percent={progress}
              strokeColor={defaultTheme.theme.palette.secondary.main}
              trailColor="#ECEFF3"
              style={{ flex: 1 }}
            />
          )}
        </StyledFilePreviewContainer>
      )}
    </div>
  );
};

export default AppUploadFile;
